import { useState } from "react";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import Switch from "@mui/material/Switch";
import { errorMessage } from "../../api/client";
import type { Player } from "../../api/models";
import { useCompleteTaskPlayer, useCreateTask, useTaskActions, useTaskOutcomes, type Task } from "../../tasks/api";
import { roundToQuarterHour } from "../../tasks/task-logic";
import { PButton } from "../../ui/PButton";
import { PConfirmDialog } from "../../ui/PConfirmDialog";
import { PDropdown } from "../../ui/PDropdown";
import { PrimeGlyph } from "../../ui/PrimeGlyph";
import { PSidebar } from "../../ui/PSidebar";
import { useToast } from "../../ui/toast";
import { mediumDate, age } from "../profile-format";
import { completeAllContactNote, taskContactNote } from "../profile-logic";
import { contactTaskBody, invalidateProfile, usePlayerTasks, type PlayerTask } from "../profile-api";

const SIDEBAR_WIDTH = { xs: "100%", md: "30rem" };
const LOG_FAILED = "The task was completed, but the contact was not added to the player's communication history.";

// THE PLAYER'S OPEN TASKS: EACH WITH ITS DAY, TIME, TITLE AND HOST, THEN OUTCOME / COMPLETED /
// NOTES AND A SAVE; "COMPLETE ALL" COMPLETES EVERY ONE AT ONCE. A SAVE THAT COMPLETES A TASK
// LOGS THE CONTACT ON THE PLAYER'S TIMELINE (LOG-ONLY, `/player/player-communication`), THEN
// OFFERS A "PLAYER CONTACT" RECORD. AS IN ANGULAR, A SINGLE SAVE DOES NOT REQUIRE AN OUTCOME
// UNLESS IT COMPLETES THE TASK, AND SHOWS NO SUCCESS TOAST.
export function TasksSidebar(props: { player: Player; open: boolean; onClose: () => void; onOfferContact: (note: string) => void }) {
  const { player, open, onClose, onOfferContact } = props;
  const toast = useToast();
  const qc = useQueryClient();
  const tasks = usePlayerTasks(player, open);
  const outcomes = useTaskOutcomes();
  const complete = useCompleteTaskPlayer();
  const [drafts, setDrafts] = useState<Record<number, PlayerTask>>({});
  const [draftsFor, setDraftsFor] = useState<PlayerTask[] | undefined>(undefined);
  const [confirmAll, setConfirmAll] = useState(false);
  const [completingAll, setCompletingAll] = useState(false);

  // RE-SEED THE EDITS WHENEVER A FRESH LIST ARRIVES
  if (tasks.data !== draftsFor) {
    setDraftsFor(tasks.data);
    setDrafts(Object.fromEntries((tasks.data ?? []).map((t) => [t.taskId, { ...t }])));
  }
  const list = (tasks.data ?? []).map((t) => drafts[t.taskId] ?? t);
  const set = (taskId: number, patch: Partial<PlayerTask>) => setDrafts((d) => ({ ...d, [taskId]: { ...d[taskId]!, ...patch } }));
  const outcomeName = (id: number | null | undefined) => outcomes.data?.find((o) => o.id === id)?.name;

  const saveOne = (pt: PlayerTask) =>
    complete.mutateAsync({
      player: { playerId: pt.playerId, completed: !!pt.completed, taskOutcomeId: pt.taskOutcomeId ?? null, taskOutcomeNote: pt.taskOutcomeNote ?? null },
      taskId: pt.taskId,
      persistedTaskActionId: pt.taskActionId ?? null,
      wasCompleted: false,
    });

  const save = async (pt: PlayerTask) => {
    try {
      const { logFailed } = await saveOne(pt);
      if (logFailed) toast("warning", "Player Communication", LOG_FAILED);
      await invalidateProfile(qc);
      onClose();
      onOfferContact(taskContactNote({ taskTitle: pt.taskTitle, outcomeName: outcomeName(pt.taskOutcomeId), taskOutcomeNote: pt.taskOutcomeNote }));
    } catch (e) {
      toast("error", "Task Player Completed", errorMessage(e));
    }
  };

  const completeAll = async () => {
    setConfirmAll(false);
    if (list.length === 0) {
      onClose();
      return;
    }
    if (list.some((t) => !t.taskOutcomeId)) {
      toast("error", "Complete All Tasks", "Please select an outcome for every task before completing them all.");
      return;
    }
    if (completingAll) return;
    setCompletingAll(true);
    // EVERY SAVE RUNS; A FAILURE IS COLLECTED, NOT THROWN, SO THE OTHERS STILL LAND
    const results = await Promise.all(
      list.map((pt) =>
        saveOne({ ...pt, completed: true }).then(
          (r) => ({ ok: true as const, logFailed: r.logFailed }),
          (e: unknown) => ({ ok: false as const, error: e }),
        ),
      ),
    );
    setCompletingAll(false);
    await invalidateProfile(qc);
    if (results.some((r) => r.ok && r.logFailed)) toast("warning", "Player Communication", LOG_FAILED);
    const failed = results.find((r) => !r.ok);
    if (failed && !failed.ok) {
      toast("error", "Complete All Tasks", errorMessage(failed.error));
      return;
    }
    onClose();
    onOfferContact(completeAllContactNote(list.map((pt) => taskContactNote({ taskTitle: pt.taskTitle, outcomeName: outcomeName(pt.taskOutcomeId), taskOutcomeNote: pt.taskOutcomeNote }))));
  };

  const busy = tasks.isFetching || completingAll;
  return (
    <>
      <PSidebar open={open} onClose={onClose} position="right" width={SIDEBAR_WIDTH} noPadding ariaLabel="Tasks">
        <div className="parent overflow-hidden">
          <div className="child mb-2 p-3">
            <div className="flex flex-row align-items-center justify-content-between">
              <div className="text-2xl font-bold">Tasks</div>
              <div className="text-2xl font-bold">
                <PButton icon="list-check" label="Complete All" hostElement disabled={busy} onClick={() => setConfirmAll(true)} />
              </div>
            </div>
          </div>
          <div className="child-grow overflow-y-auto">
            {tasks.isPending ? (
              <div className="flex align-items-center justify-content-center h-30rem">
                <span className="pi-spin text-gray-300" style={{ display: "inline-flex" }}>
                  <PrimeGlyph name="spinner" size="3rem" />
                </span>
              </div>
            ) : (
              <div className="p-3">
                {list.map((pt) => (
                  <div key={pt.taskId} className="mt-3 border-bottom-1 surface-border pt-1 pb-3">
                    <div className="font-bold text-lg">{mediumDate(pt.taskStartTime)}</div>
                    <div className="text-md">
                      {pt.taskStartTime ? dayjs(pt.taskStartTime).format("h:mm A") : ""} - {pt.taskEndTime ? dayjs(pt.taskEndTime).format("h:mm A") : ""}
                    </div>
                    <div className="font-bold mt-1 mb-3">{pt.taskTitle}</div>
                    <div>
                      <div className="font-bold">Host:</div>
                      <div>{pt.hostName}</div>
                    </div>
                    <div className="mt-3 flex flex-row align-items-center justify-content-between">
                      <div>
                        <div className="font-bold">Outcome:</div>
                        <PDropdown
                          value={pt.taskOutcomeId ?? ""}
                          options={(outcomes.data ?? []).map((o) => ({ label: o.name, value: o.id }))}
                          placeholder="Select an outcome"
                          onChange={(v) => set(pt.taskId, { taskOutcomeId: v })}
                        />
                      </div>
                      <div className="flex flex-column align-items-end justify-content-end">
                        <div className="font-bold">Completed:</div>
                        <div>
                          {pt.completed && <span className="material-symbols-outlined mr-2 text-green-500 fadein animation-duration-1000">task_alt</span>}
                          <Switch
                            checked={!!pt.completed}
                            onChange={(e) => {
                              if (e.target.checked && !pt.taskOutcomeId) {
                                toast("error", "Task Player Completed", "Please select an outcome");
                                return;
                              }
                              set(pt.taskId, { completed: e.target.checked });
                            }}
                            slotProps={{ input: { "aria-label": `${pt.taskTitle ?? "Task"} completed` } }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="font-bold">Notes:</div>
                      <textarea
                        className="p-inputtext w-full"
                        rows={5}
                        style={{ resize: "none" }}
                        placeholder="Enter any notes"
                        aria-label="Notes"
                        value={pt.taskOutcomeNote ?? ""}
                        onChange={(e) => set(pt.taskId, { taskOutcomeNote: e.target.value })}
                      />
                    </div>
                    <div className="mt-2 flex justify-content-end">
                      <PButton label="Save" hostElement disabled={busy || complete.isPending} onClick={() => void save(pt)} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PSidebar>
      <PConfirmDialog
        open={confirmAll}
        header="Complete All Tasks"
        message="Are you sure you want to complete ALL of these tasks?"
        acceptLabel="Yes"
        rejectLabel="No"
        onAccept={() => void completeAll()}
        onReject={() => setConfirmAll(false)}
      />
    </>
  );
}

// THE PLAYER, AS THE CONTACT AND NEW-TASK FORMS SHOW IT AT THE TOP
export function PlayerSummary({ player }: { player: Player }) {
  return (
    <div className="mt-3 flex flex-row align-items-start justify-content-between">
      <div>
        <div className="font-bold">{(player.name ?? "").toUpperCase()}</div>
        <div className="text-500 text-sm mt-1">{player.cardId}</div>
      </div>
      {player.birthdate && (
        <div className="text-sm flex flex-column">
          <div className="font-bold">{mediumDate(player.birthdate)}</div>
          <div className="text-500">{age(player.birthdate)} years old</div>
        </div>
      )}
    </div>
  );
}

// "PLAYER CONTACT": ACTION, OUTCOME AND NOTES, SAVED AS A COMPLETED 15-MINUTE TASK STARTING NOW
// (ROUNDED TO THE NEAREST QUARTER HOUR). OPENED ONLY FROM THE "CREATE CONTACT" OFFER.
export function ContactSidebar({ player, open, onClose, prefill }: { player: Player; open: boolean; onClose: () => void; prefill: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const actions = useTaskActions();
  const outcomes = useTaskOutcomes();
  const create = useCreateTask();
  const [actionId, setActionId] = useState(0);
  const [outcomeId, setOutcomeId] = useState(0);
  const [note, setNote] = useState(prefill);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  // RESET EACH TIME IT OPENS, WITH THAT OFFER'S NOTE
  if (open && seededFor !== prefill) {
    setSeededFor(prefill);
    setActionId(0);
    setOutcomeId(0);
    setNote(prefill);
  } else if (!open && seededFor !== null) {
    setSeededFor(null);
  }

  const save = () =>
    create.mutate(
      contactTaskBody({ start: roundToQuarterHour(dayjs()).toDate(), taskActionId: actionId, taskOutcomeId: outcomeId, taskOutcomeNote: note, playerInternalId: player.id }) as unknown as Task,
      {
        onSuccess: () => {
          toast("success", "Create Player Contact", "Player Contact was successfully created");
          void invalidateProfile(qc);
          onClose();
        },
        onError: (e) => toast("error", "Create Player Contact", errorMessage(e)),
      },
    );

  return (
    <PSidebar open={open} onClose={onClose} position="right" width={SIDEBAR_WIDTH} noPadding ariaLabel="Player Contact">
      <div className="parent overflow-hidden">
        <div className="child mb-2 p-3">
          <div className="text-2xl font-bold">Player Contact</div>
          <div>
            <PlayerSummary player={player} />
            <div className="w-full mt-4 flex flex-column">
              <label className="font-bold mb-1">Action:</label>
              <div className="w-full flex align-items-center">
                <PDropdown value={actionId || ""} options={(actions.data ?? []).map((a) => ({ label: a.name, value: a.id }))} placeholder="Select an action" onChange={setActionId} />
              </div>
            </div>
            <div className="mt-3">
              <div>
                <div className="font-bold">Outcome:</div>
                <PDropdown value={outcomeId || ""} options={(outcomes.data ?? []).map((o) => ({ label: o.name, value: o.id }))} placeholder="Select an outcome" onChange={setOutcomeId} />
              </div>
            </div>
            <div className="mt-3">
              <div className="font-bold">Notes:</div>
              <textarea className="p-inputtext w-full" rows={5} style={{ resize: "none" }} placeholder="Enter any notes" aria-label="Notes" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="mt-3">
              <PButton label="Save" className="w-full" hostElement disabled={!outcomeId || !actionId || create.isPending} loading={create.isPending} onClick={save} />
            </div>
          </div>
        </div>
      </div>
    </PSidebar>
  );
}
